import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Route, Switch } from "react-router-dom";
import { Redirect, withRouter } from "react-router";
import { IntlProvider } from "react-intl";
import { Helmet } from "react-helmet";
import { Settings } from "luxon";
import ReactGA from "react-ga";

import PrivateRoute from "./PrivateRoute";
import Navigation from "../components/navigation";
import Search from "../components/search";
import Carousel from "../components/carousel";
import Footer from "../components/footer";
import CookiesModal from "../components/cookies";
import NewsletterModal from "../components/newsletter";
import Floor from "../components/floor";
import MobileFloor from "../components/mobileFloor";
import MobileMenu from "../components/mobileMenu";
import MobileClasse from "../components/mobileClasse";

import Account from "../screens/Account";
import Artists from "../screens/Artists";
import ArtistDetails from "../screens/ArtistDetails";
import Blog from "../screens/Blog";
import Article from "../screens/Article";
import Spaces from "../screens/Spaces";
import Classes from "../screens/Classes";
import Calendar from "../screens/Calendar";
import Library from "../screens/Library";
import Home from "../screens/Home";
import Info from "../screens/Info";
import OpenCalls from "../screens/OpenCalls";
import OpenCallsResults from "../screens/OpenCallsResults";
import OpenCallsDetails from "../screens/OpenCallsDetails";
import NotFound from "../screens/Home";
import Auth from "../screens/Auth";
import SpacesBooking from "../screens/Account/Spaces/Booking";
import ArtisticsBooking from "../screens/Account/Artistics/Booking";
import ClaimBooking from "../screens/Account/Claim/Booking";
import TechnicsBooking from "../screens/Account/Technics/Booking";
import DynamicBooking from "../screens/Account/Booking";
import Privacy from "../screens/Privacy";
import Cookies from "../screens/Cookies";
import Terms from "../screens/Terms";
import Splash from "../screens/Splash";

import config from "../constants/config";
import { appLocales, defaultLanguage } from "../localization";
import { changeLocale, changeRegion } from "../redux/actions/app";
import { manualUser, updateUserMe } from "../redux/actions/me";
import { getMe } from "../redux/selectors/me";
import { getUrl, isTouch } from "../utils/helper";
import FaqModal from "../components/faqModal";
import LessonDetails from "../screens/LessonDetails";
import EventDetails from "../screens/EventDetails";

ReactGA.initialize(config.ANALYTICS_ID, {
    debug: config.DEV,
});

class Routes extends Component {
    constructor(props) {
        super(props);

        if (window.user) {
            this.props.manualUser(window.user);
        }

        this.region = null;
        this.locale = null;

        let userLocale =
            navigator.language || navigator.userLanguage || defaultLanguage;
        let userLocaleArray = userLocale.toLowerCase().split(/[_-]+/);
        if (userLocaleArray.length > 1) {
            this.region = userLocaleArray[1];
        }

        let defaultLocale = this.props.locale || userLocaleArray[0];

        let lang =
            appLocales.length > 1
                ? window.location.pathname.split("/")[1] || defaultLocale
                : defaultLocale;

        if (appLocales.length > 1 && appLocales.indexOf(lang) === -1) {
            this.props.changeLocale(defaultLanguage);
            this.locale = defaultLanguage;
            this.props.history.push(defaultLanguage + "/" + lang);
            Settings.defaultLocale = defaultLanguage;
        } else {
            this.props.changeLocale(lang);
            this.locale = lang;
            Settings.defaultLocale = lang;
        }
    }

    componentDidMount() {
        ReactGA.pageview(
            this.props.location.pathname + this.props.location.search
        );
    }

    componentDidUpdate(prevProps) {
        if (!this.props.isLoggingIn && prevProps.isLoggingIn) {
            if (this.props.user) {
                if (isTouch()) {
                    this.props.history.replace(getUrl("account"));
                } else {
                    this.props.history.replace(getUrl("account/profile"));
                }
            }
        }

        if (this.props.user && !prevProps.user) {
            if (this.props.user.locale !== this.locale) {
                this.props.updateUserMe({ locale: this.locale });
            }

            ReactGA.set({ userId: this.props.user.id });
        }

        if (
            this.props.location.pathname !== prevProps.location.pathname ||
            this.props.location.search !== prevProps.location.search
        ) {
            ReactGA.pageview(
                this.props.location.pathname + this.props.location.search
            );
        }
    }

    render() {
        const { user } = this.props;

        if (!this.locale) {
            return null;
        }

        let region = this.region ? "-" + this.region : "";
        let locale = this.locale + region;

        let baseClass = "absolute top-full w-full -mt-60 xl:-mt-70";

        if (
            location.pathname.includes(getUrl("account")) ||
            location.pathname.includes(getUrl("privacy")) ||
            location.pathname.includes(getUrl("terms")) ||
            location.pathname.includes(getUrl("calendar")) ||
            location.pathname.includes(getUrl("cookies"))
        ) {
            baseClass = "w-full";
        }

        return (
            <IntlProvider locale={locale}>
                <Helmet
                    titleTemplate={"%s | " + config.APP_NAME}
                    defaultTitle={config.APP_NAME}
                />
                <Navigation pathname={this.props.location.pathname} />
                <Search />
                <Carousel
                    history={this.props.history}
                    pathname={this.props.location.pathname}
                />
                <div className={baseClass}>
                    <Switch>
                        <Route
                            exact
                            path={"/"}
                            render={() =>
                                this.props.history.replace("/" + this.locale)
                            }
                        />
                        <Route exact path={"/:lang"} component={Home} />
                        <Route
                            exact
                            path={"/:lang/artists"}
                            component={Artists}
                        />
                        <Route
                            exact
                            path={"/:lang/artists/:slug"}
                            component={ArtistDetails}
                        />
                        <Route
                            exact
                            path={"/:lang/lessons/:slug"}
                            component={LessonDetails}
                        />
                        <Route
                            exact
                            path={"/:lang/events/:slug"}
                            component={EventDetails}
                        />
                        <Route
                            exact
                            path={"/:lang/calendar"}
                            component={Calendar}
                        />
                        <Route
                            exact
                            path={"/:lang/library"}
                            component={Library}
                        />
                        <Route
                            exact
                            path={"/:lang/bookmarks/:category?"}
                            component={Blog}
                        />
                        <Route
                            exact
                            path={"/:lang/bookmarks/:category/:slug"}
                            component={Article}
                        />
                        <Route
                            exact
                            path={"/:lang/spaces_booking"}
                            component={Spaces}
                        />
                        <Route
                            exact
                            path={"/:lang/practices/:date?"}
                            component={Classes}
                        />
                        <Route
                            exact
                            path={"/:lang/about/:type?"}
                            component={Info}
                        />
                        <PrivateRoute
                            exact
                            path={[
                                "/:lang/account",
                                "/:lang/account/profile",
                                "/:lang/account/profile/edit",
                                "/:lang/account/artistic",
                                "/:lang/account/claim",
                                "/:lang/account/technical",
                                "/:lang/account/spaces",
                            ]}
                            component={Account}
                        />
                        <Route
                            exact
                            path={"/:lang/privacy"}
                            component={Privacy}
                        />
                        <Route
                            exact
                            path={"/:lang/cookies"}
                            component={Cookies}
                        />
                        <Route exact path={"/:lang/terms"} component={Terms} />
                        <Route
                            exact
                            path={"/:lang/opencalls/:type?"}
                            component={OpenCalls}
                        />
                        <Route
                            exact
                            path={"/:lang/opencalls/:type/:category"}
                            component={OpenCallsDetails}
                        />
                        <PrivateRoute
                            exact
                            path={"/:lang/opencalls/:type/results"}
                            component={OpenCallsResults}
                        />
                        <Route path="*" component={NotFound} />
                    </Switch>
                    <FaqModal />
                    <Floor />
                    <MobileFloor />
                    <MobileClasse />
                    <MobileMenu pathname={this.props.location.pathname} />
                    {user && <SpacesBooking />}
                    {user && <ArtisticsBooking />}
                    {user && <ClaimBooking />}
                    {user && <TechnicsBooking />}
                    {user && <DynamicBooking />}
                    <Splash />
                    <CookiesModal locale={this.locale} />
                    <NewsletterModal />
                    <Auth location={this.props.location} />
                    <Footer pathname={this.props.location.pathname} />
                </div>
            </IntlProvider>
        );
    }
}

const mapStateToProps = (state) => ({
    locale: state.app.locale,
    user: getMe(state),
    isLoggingIn: state.me.isLoggingIn,
});

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators(
        {
            manualUser,
            changeLocale,
            changeRegion,
            updateUserMe,
        },
        dispatch
    );
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Routes));
